import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { LANGUAGES, orderedLanguages, getDefaultLangCode } from '../i18n/languages';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from '../lib/utils';

/**
 * Language selector dropdown (flag + label).
 * `onChange(code)` is called with the chosen language code; the caller decides
 * how to persist it (login choice vs operator override).
 * The system default language (set by the Gestore) is always listed first.
 */
export const LanguageSwitcher = ({ onChange, variant = 'default', className, defaultCode }) => {
  const { i18n } = useTranslation();
  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];
  const dft = defaultCode || getDefaultLangCode();
  const list = orderedLanguages(dft);

  const handle = (code) => {
    i18n.changeLanguage(code);
    onChange && onChange(code);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="language-switcher"
          className={cn(
            'inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 h-10 text-sm font-medium hover:bg-muted transition-colors',
            variant === 'compact' && 'h-9 px-2.5',
            className
          )}
        >
          <span className="text-base leading-none" aria-hidden>{current.flag}</span>
          {variant !== 'icon' && <span className="hidden sm:inline">{current.label}</span>}
          {variant === 'icon' && <Globe className="h-4 w-4 text-muted-foreground" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {list.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`language-option-${l.code}`}
            onClick={() => handle(l.code)}
            className="gap-2 cursor-pointer"
          >
            <span className="text-base leading-none" aria-hidden>{l.flag}</span>
            <span className="flex-1">{l.label}</span>
            {l.code === dft && current.code !== l.code && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">•</span>
            )}
            {current.code === l.code && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
